import { AbstractStore }              from 'modelx-jts-common';
import { ApiCollection }              from 'modelx-jts-common';
import { action }                     from 'mobx';
import { computed }                   from 'mobx';
import { observable }                 from 'mobx';
import { toJS }                       from 'mobx';
import UserGroupCompanyModel          from 'models/UserGroupCompanyModel';
import AccountLsdModel                from 'models/AccountLsdModel';
import GroupLsdModel                  from 'models/GroupLsdModel';
import store                          from './index';
import CompanyPaymentVoucherStepModel from 'models/CompanyPaymentVoucherStepModel';
import CompanyPurchaseOrderStepModel  from 'models/CompanyPurchaseOrderStepModel';
import getRolesGranted                from 'constants/RolesHierarchy';
import CurrencyModel                  from 'models/CurrencyModel';
import CompanyModel                   from 'models/CompanyModel';
import ExpenseGroupModel              from 'models/ExpenseGroupModel';
import { jsTools }                    from 'tools';
import MyExpenseToProcessStatsModel   from 'models/MyExpenseToProcessStatsModel';
import VatModel                       from 'models/VatModel';
import ServiceModel                   from 'models/fondationsModels/ServiceModel';
import UserApiCollection              from 'models/collections/UserApiCollection';
import UserMeRightsModel              from 'models/fondationsModels/UserMeRightsModel';
import UserModel                      from 'models/fondationsModels/UserModel';

export class AppStore extends AbstractStore {

	/**
	 * Collections for current user and menu
	 */
	public currentUserCompanyCollection = new ApiCollection(CompanyModel);
	public currentUserGroupCompanyCollection = new ApiCollection(UserGroupCompanyModel);

	/**
	 * Current company
	 */
	public currentCompany = new CompanyModel();

	/**
	 * Collections for current company
	 * All users, all userGroupCompanies, all groups and all steps
	 */
	public currentCompanyExpenseGroupCollection = new ApiCollection(ExpenseGroupModel);

	public currentCompanyUserCollection = new UserApiCollection();

	public currentCompanyEnabledUserCollection = new UserApiCollection();
	public currentCompanyEnabledUserGroupCompanyCollection = new ApiCollection(UserGroupCompanyModel);

	// @todo: remove and use instead groupCollection
	public currentCompanyGroupCollection = new ApiCollection(GroupLsdModel);

	public currentCompanyPaymentVoucherStepCollection = new ApiCollection(CompanyPaymentVoucherStepModel);
	public currentCompanyPurchaseOrderStepCollection = new ApiCollection(CompanyPurchaseOrderStepModel);

	public currentCompanyVatCollection = new ApiCollection(VatModel);

	/**
	 * Collections for app
	 */
	public currencyCollection = new ApiCollection(CurrencyModel);

	// Utilisé pour les compteurs de notifications dans le menu
	public expenseToProcessStats = new MyExpenseToProcessStatsModel();

	public serviceCollection = new ApiCollection(ServiceModel);

	/**
	 * Ordered helpers objects
	 */
	@observable
	public orderedGroupById = {};
	@observable
	public orderedUserById = {};
	@observable
	public orderedUserByGroup = {};
	@observable
	public orderedUserGroupCompanyCollection = {};

	public ordered = false;

	public history;

	@observable
	public isLoading = false;

	/**
	 * currentUserId
	 */
	private _currentUserId: null | number = null;

	/**
	 * currentCompanyId set from this.props.match.params
	 */
	@observable
	private _currentCompanyId: null | number = null;

	/**
	 * currentExpenseGroupId set from this.props.match.params
	 */
	@observable
	private _currentExpenseGroupId: null | id = null;

	/**
	 * last loaded companyId
	 */
	private companyIdForCollections: null | number = null;

	/**
	 * last loaded companyId too
	 */
	private companyIdForStepCollections: null | number = null;

	@observable
	private userMeRights = new UserMeRightsModel();

	@computed
	public get currentCompanyCurrency(): CurrencyModel {
		const currentCompanyCurrency = this.currencyCollection.find(
			currency => currency.get('@id') === this.currentCompany.get('currency.@id')
		);
		return !!currentCompanyCurrency ? currentCompanyCurrency as CurrencyModel : new CurrencyModel();
	}

	@computed
	public get orderedCurrentCompanyEnabledUsers(): AccountLsdModel[] {
		return [
			...this.currentCompanyEnabledUserCollection.models,
		] as AccountLsdModel[];
	}

	public clearStore() {
		this.currentUserCompanyCollection.clear();
		this.currentUserGroupCompanyCollection.clear();
		this.currentCompany.clear();
		this.currentCompanyExpenseGroupCollection.clear();
		this.currentCompanyUserCollection.clear();
		this.currentCompanyEnabledUserCollection.clear();
		this.currentCompanyEnabledUserGroupCompanyCollection.clear();
		this.currentCompanyGroupCollection.clear();
		this.currentCompanyPaymentVoucherStepCollection.clear();
		this.currentCompanyPurchaseOrderStepCollection.clear();
		this.currentCompanyVatCollection.clear();
		this.expenseToProcessStats.clear();
		this.userMeRights.clear();

		this.orderedGroupById = {};
		this.orderedUserById = {};
		this.orderedUserByGroup = {};
		this.orderedUserGroupCompanyCollection = {};

		this.ordered = false;

		this._currentUserId = null;
		this._currentCompanyId = null;
		this._currentExpenseGroupId = null;
		this.companyIdForCollections = null;
		this.companyIdForStepCollections = null;
	}

	public set currentUserId(id: number) {
		if (this._currentUserId !== id) {
			const previousCurrentUserId = this._currentUserId;
			this._currentUserId = id;
			this.loadAllCurrentUserAndMenuData(id)
				.catch(
				() => this._currentUserId = previousCurrentUserId
			);
		}
	}

	public get currentUserId() {
		return this._currentUserId;
	}

	@action
	public setCurrentCompanyId(id: string | number): Promise<any> {
		return this.handleSetCurrentCompanyId(id);
	}

	/** @deprecated */
	public set currentCompanyId(id: number) {
		this.setCurrentCompanyId(id);
	}

	public get currentCompanyId() {
		return this._currentCompanyId;
	}

	@action
	public setCurrentExpenseGroupId(id: id) {
		this._currentExpenseGroupId = id;
	}

	/** @deprecated */
	public set currentExpenseGroupId(id: id) {
		this.setCurrentExpenseGroupId(id);
	}

	public get currentExpenseGroupId() {
		return this._currentExpenseGroupId;
	}

	@computed
	public get currentExpenseGroup() {
		return this.currentCompanyExpenseGroupCollection.find(e => e.id + '' === this.currentExpenseGroupId + '');
	}

	@action
	public setIsLoading = (loading: boolean) => {
		this.isLoading = loading;
	}

	@action
	public loadAllCurrentUserAndMenuData = async (userId: id = this.currentUserId): Promise<void> => {
		this.userMeRights.setId(userId);

		await Promise.all([
			this.currentUserCompanyCollection
				.setFilter('userGroupCompanies.user', userId)
				.setFilter('enabled', true)
				.addSort('userSync.fullName', true)
				.list(),

			this.currentUserGroupCompanyCollection
				.setFilter('user', userId)
				.list(),

			this.serviceCollection
				.list(),

			this.userMeRights
				.fetch(),
		]);
	}

	@action
	public loadCurrentCompany = async (
		companyId = this.currentCompanyId
	): Promise<void> => {
		await this.currentCompany
			.set('id', companyId)
			.fetch();
	}

	@action
	public loadCurrentCompanyData = async (
		companyId = this.currentCompanyId,
		force: boolean = false
	): Promise< void> => {
		if (
			!force
			&& this.companyIdForCollections + '' === companyId + ''
		) {
			return;
		}

		this.currentCompanyExpenseGroupCollection.clear();

		const previousCompanyId = this.companyIdForCollections;
		this.companyIdForCollections = companyId;

		const promises: Promise<any>[] = [
			this.currentCompanyExpenseGroupCollection
				.setFilter('archived', false)
				.setFilter('company', companyId)
				.addSort('position', true)
				.list(),

			this.currentCompanyUserCollection
				.addSort('userSync.firstName', true)
				.addSort('userSync.lastName', true)
				.setFilter('userGroupCompanies.company', companyId)
				.list(),

			this.currentCompanyEnabledUserCollection
				.addSort('userSync.firstName', true)
				.addSort('userSync.lastName', true)
				.setFilter('userGroupCompanies.company', companyId)
				.setFilter('userSync.enabled', true)
				.list(),

			this.currentCompanyEnabledUserGroupCompanyCollection
				.setFilter('company', companyId)
				.setFilter('user.userSync.enabled', true)
				.list(),

			this.currentCompanyVatCollection
				.setFilter('company', companyId)
				.addSort('rate', false)
				.list(),
		];

		if (
			!this.currentCompanyGroupCollection.isLoaded
			&& !this.currentCompanyGroupCollection.isLoading
		) {
			promises.push(this.currentCompanyGroupCollection.list());
		}

		if (
			!this.currencyCollection.isLoaded
			&& !this.currencyCollection.isLoading
		) {
			promises.push(this.currencyCollection.list());
		}

		try {
			await Promise.all(promises);

			store.appStore.orderAllCollection();
		} catch {
			this.companyIdForCollections = previousCompanyId;
		}
	}

	@action
	public fetchStepsCollection = async (companyId = this.currentCompanyId): Promise<void> => {
		if (this.companyIdForStepCollections + '' === companyId + '') {
			return;
		}

		const previousCompanyId = this.companyIdForStepCollections;
		this.companyIdForStepCollections = companyId;

		try {
			await Promise.all([
				this.currentCompanyPaymentVoucherStepCollection
					.addSort('paymentVoucherStep.position', true)
					.setFilters({ company: companyId })
					.list(),

				this.currentCompanyPurchaseOrderStepCollection
					.addSort('purchaseOrderStep.position', true)
					.setFilters({ company: companyId })
					.list(),

			]);
		} catch {
			this.companyIdForStepCollections = previousCompanyId;
		}
	}

	public getCurrentUserGroup = (user: AccountLsdModel) => {
		if (!user) {
			return null;
		}

		if (!this.ordered) {
			this.orderAllCollection();
		}

		let currentUserGroup = null;

		const currentUserGroupCompany = this.orderedUserGroupCompanyCollection[user.id];

		if (!!currentUserGroupCompany) {
			currentUserGroup = this.orderedGroupById[currentUserGroupCompany.get('group.id')];
		}

		return currentUserGroup;
	}

	public getUserByIri = (userIri: object | string): AccountLsdModel | null => {
		if (typeof userIri === 'object') {
			userIri = toJS(userIri)['@id'];
		}

		if (typeof userIri !== 'string') {
			return null;
		}

		return this.currentCompanyUserCollection.find(
			(user: AccountLsdModel) => user.get('@id') === userIri
		) as AccountLsdModel | null;
	}

	public getUsersGranted = (role: string, atLeast = true): AccountLsdModel[] => {
		const rolesGranted = getRolesGranted(role, atLeast);
		const users = [];

		this.currentCompanyGroupCollection.forEach((group: GroupLsdModel) => {
			if (rolesGranted.indexOf(group.get('reference')) >= 0) {
				if (this.orderedUserByGroup[group.id]) {
					// users = users.concat(this.orderedUserByGroup[group.id]);
					this.orderedUserByGroup[group.id].forEach(user => {
						jsTools.pushIfNotExist(users, user, (a, b) => a.id === b.id);
					});
				}
			}
		});

		// // add admins
		// this.currentCompanyUserCollection.map(user => {
		// 	user.get('roles', []).forEach(userRole => {
		// 		if (!!userRole.match(/LSD/g) && !!userRole.match(/ADMIN/g)) {
		// 			jsTools.pushIfNotExist(users, user, (a, b) => a.id === b.id);
		// 		}
		// 	});
		// });

		// users.forEach((user: AccountLsdModel) => user.userFetchAsync());

		// sort by firstName
		users.slice().sort((a: UserModel, b: UserModel) => {
			if (a.get('firstName') > b.get('firstName')) {
				return 1;
			}
			if (a.get('firstName') < b.get('firstName')) {
				return -1;
			}
			return 0;
		});

		return users;
	}

	public getCurrencyByIri = (currencyIri: string): CurrencyModel | undefined => {
		return this.currencyCollection.find(currency => {
			return currency.get('@id') === currencyIri;
		}) as CurrencyModel | undefined;
	}

	private handleSetCurrentCompanyId = async (companyId: string | number): Promise<void> => {
		if (
			!!companyId
			&& this._currentCompanyId + '' === companyId + ''
		) {
			return;
		}

		this.setIsLoading(true);

		const previousCompanyId = this._currentCompanyId;

		if (typeof companyId === 'number') {
			this._currentCompanyId = companyId;
		} else if (typeof companyId === 'string' && !isNaN(parseInt(companyId, 0))) {
			this._currentCompanyId = parseInt(companyId, 0);
		} else {
			this.setIsLoading(false);
			return;
		}

		try {
			await Promise.all([
				this.loadCurrentCompany(this._currentCompanyId),
				this.loadCurrentCompanyData(this._currentCompanyId),
				this.fetchStepsCollection(this._currentCompanyId)
			]);
		} catch {
			this._currentCompanyId = previousCompanyId;
		}

		this.setIsLoading(false);
	}

	@action
	private orderAllCollection() {
		this.ordered = false;
		this.orderGroupById();
		this.orderUserById();
		this.orderUserGroupCompanyCollection();
		this.ordered = true;
		return true;
	}

	@action
	public orderGroupById() {
		this.orderedGroupById = {};
		this.currentCompanyGroupCollection.forEach(group => {
			this.orderedGroupById[group.id] = group;
		});
	}

	@action
	private orderUserById() {
		this.orderedUserById = {};
		this.currentCompanyUserCollection.forEach((user: AccountLsdModel) => {
			this.orderedUserById[user.id] = user;
		});
	}

	@action
	private orderUserGroupCompanyCollection() {
		this.orderedUserGroupCompanyCollection = {};
		this.orderedUserByGroup = {};
		this.currentCompanyEnabledUserGroupCompanyCollection.forEach(userGroupCompany => {
			const cugc = this.orderedUserGroupCompanyCollection[userGroupCompany.get('user.id')];

			if (
				!!cugc && cugc.get('group.priority', 0) < userGroupCompany.get('group.priority', 0)
				|| !cugc
			) {
				this.orderedUserGroupCompanyCollection[userGroupCompany.get('user.id')] = userGroupCompany;
			}

			// ------------ ------------ ------------

			if (!this.orderedUserByGroup[userGroupCompany.get('group.id')]) {
				this.orderedUserByGroup[userGroupCompany.get('group.id')] = [];
			}

			this.orderedUserByGroup[userGroupCompany.get('group.id')].push(
				this.orderedUserById[userGroupCompany.get('user.id')]
			);
		});
	}
}

export default new AppStore();
